import { useRef, useState, useCallback } from 'react';

/**
 * useBatchedState - Buffers state updates and applies them after a short delay.
 * Useful when multiple updates happen in rapid succession and you want to reduce re-renders.
 *
 * @param initialValue - Initial state value
 * @param delay - Debounce delay in ms (default 50ms)
 */
export function useBatchedState<T extends object | any[]>(initialValue: T, delay = 50) {
	const [state, setState] = useState<T>(initialValue);
	const bufferRef = useRef<T>(initialValue);
	const timerRef = useRef<NodeJS.Timeout | null>(null);

	const isArray = Array.isArray(initialValue);

	const updateState = useCallback(
		(updates: Partial<T> | ((prev: T) => T)) => {
			if (typeof updates === 'function') {
				bufferRef.current = updates(bufferRef.current);
			} else {
				bufferRef.current = isArray
					? (updates as T) // replace the array entirely
					: ({
							...(bufferRef.current as object),
							...(updates as object),
					  } as T);
			}

			if (!timerRef.current) {
				timerRef.current = setTimeout(() => {
					setState(bufferRef.current);
					timerRef.current = null;
				}, delay);
			}
		},
		[delay, isArray]
	);

	return [state, updateState] as const;
}
